import React, { useState } from 'react';
import { backendApi } from '../api/backendApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Package, Mail, Phone, Star, Edit, Trash2 } from 'lucide-react';
import { Button } from '../Components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../Components/ui/card';
import { Input } from '../Components/ui/input';
import { Badge } from '../Components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../Components/ui/dialog';
import { Label } from '../Components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Components/ui/select';
import { Textarea } from '../Components/ui/textarea';
import { Avatar, AvatarFallback } from '../Components/ui/avatar';

export default function Vendors() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const queryClient = useQueryClient();

  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => backendApi.entities.Vendor.list('-created_date', 100),
    initialData: []
  });

  const createMutation = useMutation({
    mutationFn: (data) => backendApi.entities.Vendor.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['vendors']);
      setIsDialogOpen(false);
      setEditingVendor(null);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => backendApi.entities.Vendor.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['vendors']);
      setIsDialogOpen(false);
      setEditingVendor(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.Vendor.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['vendors']);
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    if (editingVendor) {
      updateMutation.mutate({ id: editingVendor.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    return vendor.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           vendor.contact_person?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const statusColors = {
    'Active': 'bg-green-100 text-green-800 border-green-300',
    'Inactive': 'bg-gray-100 text-gray-800 border-gray-300',
    'Preferred': 'bg-purple-100 text-purple-800 border-purple-300'
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 rounded-lg">
              <Package className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
              <p className="text-sm text-gray-500">Manage your recruitment vendors</p>
            </div>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => setEditingVendor(null)}
                className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 shadow-md"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input name="company_name" defaultValue={editingVendor?.company_name} required />
                  </div>
                  <div>
                    <Label>Contact Person *</Label>
                    <Input name="contact_person" defaultValue={editingVendor?.contact_person} required />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" name="email" defaultValue={editingVendor?.email} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input name="phone" defaultValue={editingVendor?.phone} />
                  </div>
                  <div>
                    <Label>Specialization</Label>
                    <Input name="specialization" defaultValue={editingVendor?.specialization} />
                  </div>
                  <div>
                    <Label>Rating (1-5)</Label>
                    <Input type="number" min="1" max="5" name="rating" defaultValue={editingVendor?.rating} />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select name="status" defaultValue={editingVendor?.status || 'Active'}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                        <SelectItem value="Preferred">Preferred</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Textarea name="notes" defaultValue={editingVendor?.notes} rows={3} />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingVendor ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10" />
        <Input
          placeholder="Search vendors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12 bg-white shadow-sm border-gray-200"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVendors.map((vendor) => (
          <Card key={vendor.id} className="hover:shadow-lg transition-all duration-300 border border-gray-200">
            <CardHeader className="pb-3">
              <div className="flex items-start space-x-3">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-orange-600 text-white font-semibold">
                    {vendor.company_name?.substring(0, 2).toUpperCase() || 'VN'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg truncate">{vendor.company_name}</CardTitle>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge className={`${statusColors[vendor.status]} border`}>
                      {vendor.status}
                    </Badge>
                    {vendor.rating && (
                      <div className="flex items-center text-yellow-500">
                        <Star className="h-3 w-3 fill-current" />
                        <span className="text-xs ml-1">{vendor.rating}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditingVendor(vendor);
                      setIsDialogOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (window.confirm('Are you sure?')) {
                        deleteMutation.mutate(vendor.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center text-sm text-gray-600">
                <Mail className="h-4 w-4 mr-2" />
                <span className="truncate">{vendor.contact_person}</span>
              </div>
              {vendor.email && (
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2" />
                  <span className="truncate">{vendor.email}</span>
                </div>
              )}
              {vendor.phone && (
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2" />
                  {vendor.phone}
                </div>
              )}
              {vendor.specialization && (
                <div className="text-sm font-medium text-orange-600">
                  {vendor.specialization}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredVendors.length === 0 && (
        <Card className="shadow-sm">
          <CardContent className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
            <p className="text-gray-500">Add your first vendor to get started</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}